import ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import {
  type EditorModel,
  type EditorModelWithoutRelations,
} from '@/lib/types';
import { useContext, useState } from 'react';
import ModelsContext from '../../../ModelsContext';
import Dialog from '@/components/Dialog';
import useApi from '@/lib/api/useApi';
import { Checkbox, FormControlLabel } from '@mui/material';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import Space from '@/components/Space';
import { FormattedMessage } from 'react-intl';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import DeactivationInfo from './DeactivationInfo';
import DefaultModelWarning from './DefaultModelWarning';
import moment from 'moment';

type ModelActivationControlsProps = {
  model: EditorModel;
};

const ModelActivationControls = ({ model }: ModelActivationControlsProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);

  const [modelIsActive, setModelIsActive, , reenableModelIsActiveSync] =
    useComplexSwrState(model.deactivatedAt === null);
  const [saving, setSaving] = useState(false);

  const handleActivationToggle = async (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ): Promise<void> => {
    setModelIsActive(checked);

    setSaving(true);

    const updatedModel = await api.put<EditorModelWithoutRelations>(
      `editor/challenge-model/models/${model.id}/is-active`,
      {
        isActive: checked,
      },
    );

    setModels(models => {
      models[model.id] = {
        ...models[model.id],
        ...updatedModel,
      };
    });

    setSaving(false);

    reenableModelIsActiveSync();
  };

  return (
    <div>
      <h3>
        <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.activation_controls.title" />
      </h3>
      <Dialog.ContentText>
        <FormattedMessageWithChallengeNaming
          id={`editor.model_editor.model_selector.model_settings.dialog.activation_controls.text.${model.type === ModelType.CHALLENGE ? 'challenge' : 'task'}`}
          hasHtml
        />
      </Dialog.ContentText>
      <Space height={6} />
      <FormControlLabel
        control={
          <Checkbox
            checked={modelIsActive}
            onChange={handleActivationToggle}
            name="is-active"
            disabled={saving || model.isDefault}
          />
        }
        label={
          <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.activation_controls.active_toggle.label" />
        }
      />
      <Space height={6} />
      {model.isDefault && <DefaultModelWarning />}
      {!modelIsActive && model.deactivatedAt !== null && (
        <DeactivationInfo>
          <FormattedMessage
            id="editor.model_editor.model_selector.model_settings.dialog.activation_controls.deactivation_info"
            values={{
              modelDeactivatedAt: moment(model.deactivatedAt).format('L'),
            }}
          />
        </DeactivationInfo>
      )}
    </div>
  );
};

export default ModelActivationControls;
